import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import type { ProcessorPoolListResponseDto, ProcessorPoolMemberDto } from '@/types/dtos/processor/processor-pool.dto';

export const LIST_PROCESSOR_POOL_QUERY_KEY = 'processor-pool';

export function filterProcessorsByStatus(
  processors: ProcessorPoolMemberDto[],
  filter: string,
): ProcessorPoolMemberDto[] {
  switch (filter) {
    case 'connected':
      return processors.filter((p) => p.isConnected);
    case 'offline':
      return processors.filter((p) => !p.isConnected);
    case 'busy':
      return processors.filter((p) => Boolean(p.currentJobId));
    case 'maintenance':
      return processors.filter((p) => p.opsStatus === 'maintenance' || p.maintenanceMode);
    case 'paused':
      return processors.filter((p) => p.opsStatus === 'paused');
    case 'auth':
      return processors.filter((p) => p.authHealth === 'jwt_stale');
    default:
      return processors;
  }
}

const listProcessorPool = async (token: string): Promise<ProcessorPoolMemberDto[]> => {
  const url = `${import.meta.env.VITE_API_URL}/processor/pool`;
  const response = (await sendJSONRequest({ url, method: METHOD.GET, token })) as ProcessorPoolListResponseDto;
  return (response.processors ?? []).map((p) => ({
    ...p,
    deployment: p.deployment ?? (p.vms?.length ? 'azure' : 'standalone'),
    opsStatus: p.opsStatus ?? 'active',
    vms: p.vms ?? [],
  }));
};

export function useListProcessorPoolQuery() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [LIST_PROCESSOR_POOL_QUERY_KEY],
    queryFn: async () => {
      const token = await getToken();
      return listProcessorPool(token ?? '');
    },
    refetchInterval: 15_000,
  });
}
